import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SelectedOptions, SelectedOptionsEvent } from '@app/product/reducers/product.reducer';
import { NewRelic } from '@app/shared/services/newrelic.service';
import {
	FormErrors,
	ValidationMessages,
	formValidationErrors$,
	formValidationHelper,
} from '@app/shared/utils/form-helper';
import { I18nService } from '@app/shared/utils/i18n.service';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';

type OptionValues = Record<string, { locale?: unknown }>;

// TODO: Remove and use DTOs.
export interface Selectable {
	id?: number;
	key: string;
	type: string;
	is_metadata?: boolean;
	locale?: { name?: LocaleValue; description?: LocaleValue };
	properties?: {
		optionValues?: OptionValues;
	};
}

interface Variable {
	type: 'select' | 'number' | 'text';
	key: string;
	value: unknown;
	label: string;
	placeholder?: string;
	unit?: string;
	hidden?: boolean;
	show?: boolean;
	keyValuePairs?: { key: string; value: unknown }[];
	minValue?: number;
	maxValue?: number;
	selectableValues?: Selectable[];
}

const VISIBLE_IN_VALIDATION_STEP = new Set(['material', 'quantity', 'order_quantity']);

/** Product option selector component. */
@UntilDestroy()
@Component({
	selector: 'g-product-option-selector',
	templateUrl: './product-option-selector.component.html',
	styleUrls: ['./product-option-selector.component.scss'],
	standalone: false,
})
export class ProductOptionSelectorComponent implements OnInit {
	private readonly fb = inject(UntypedFormBuilder);
	private readonly newRelic = inject(NewRelic);
	private readonly i18n = inject(I18nService);

	@Input({ required: true }) readonly product!: api.ProductDto;
	@Input() readonly initialOptions?: SelectedOptions;
	@Input() readonly readOnly = false;
	@Input() readonly disabled = false;
	@Input() readonly showHeader = true;
	@Input() readonly truncateDropdownText = false;
	@Input() readonly addPaddingToOptionItem = false;
	@Input() readonly isValidationStep = false;
	@Input() readonly setToBottom: string[] = [];
	@Output() readonly selectedOptionsChanged = new EventEmitter<SelectedOptionsEvent>();
	productOptionsForm!: UntypedFormGroup;
	validationMessages!: ValidationMessages;
	formErrors$!: FormErrors;
	requiredFields!: Set<string>;
	productVariables!: Variable[];
	readonly keys = Object.keys;
	readonly icons = { faChevronDown };

	private readonly customValidationMessages: Record<
		string,
		// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
		{ [type: string]: string | Function; outOfRange: (min?: number, max?: number) => string }
	>;

	constructor() {
		this.customValidationMessages = {
			number: {
				required: $localize`:@@EditorNumberRequired:Required`,
				invalid: $localize`:@@EditorNumberInvalid:Must be a number`,
				outOfRange: (min?: number, max?: number) =>
					$localize`:@@EditorNumberOutOfRange:Give a number between` + ` ${min ?? '0'}-${max ?? '0'}`,
				integerOnly: $localize`:@@EditorNumberIntegerOnly:Give a number without decimals`,
			},
		};
	}

	/** Initialize the component. */
	ngOnInit(): void {
		this.validationMessages = {};
		this.prepareVariables();
		this.buildForm();
		this.emitSelectedOptionsChange();
	}

	/**
	 * Get a function that returns label text for dropdown button based on selected option
	 * @param variable Object containing info about a selectable option (from this.productVariables)
	 * @returns Function which returns text which will be shown in the dropdown button
	 */
	getDropdownButtonText(variable: Variable): () => string {
		return () => {
			// Values coming from PIM are strings, but g-select component returns
			// integer values as integer, so cast all values to strings.
			const selected = `${this.productOptionsForm.value[variable.key]}`;
			let selectedText = variable.keyValuePairs?.find?.(({ key }) => key === selected)?.value as string;
			selectedText =
				selectedText && typeof variable.unit === 'string' && variable.unit
					? `${selectedText} ${variable.unit}`
					: selectedText;
			return selectedText || `${variable.placeholder} (${$localize`:@@EditorNotChosen:not chosen`})`;
		};
	}

	/**
	 * Get a function that returns text for dropdown option based on provided parameters
	 * @param variable Object containing info about a selectable option (from this.productVariables)
	 * @returns Function which returns text which will be shown in the dropdown option
	 */
	getDropdownOptionText(variable: Variable): (params: { option: { value: string } }) => string {
		return (params) => {
			const option = params?.option?.value;
			const unit = (variable?.unit?.length ?? 0) > 0 && variable.unit;
			return unit ? `${option} ${unit}` : option;
		};
	}

	/**
	 * Change form value when Bootstrap dropdown selection changes
	 * @param variable Object containing info about the dropdown (from this.productVariables)
	 * @param selectedValue Key of the value selected
	 */
	patchSelectedValueToForm(variable: Variable, selectedValue: unknown): void {
		this.productOptionsForm.patchValue({ [variable.key]: selectedValue });
	}

	/**
	 * Generate a unique identifier usable as a CSS class. Identifier is created from the variable key
	 * combined with select option key (when applicable). This identifier is needed for E2E tests.
	 * @param variable Object containing info about the product variable
	 * @param option Object containing info about select element option
	 * @param option.key Key of the select element option
	 * @returns CSS class
	 */
	getInputIdentifierClass(variable: Variable, option?: { key?: string }): string {
		const optionKey = option ? `_${option.key}` : '';
		return `autoid_${variable.key}${optionKey}`
			.toLowerCase()
			.replaceAll(' ', '_')
			.replaceAll('_', '-')
			.replaceAll(/[^\da-z-]/g, '');
	}

	/**
	 * Prepares the variables for the product option selector component.
	 * This method filters out metadata properties from the product's product_properties,
	 * and then handles each selectable variable based on its type.
	 * It also hides variables other than quantity and material when in the validation step.
	 */
	private prepareVariables() {
		const selectables = ((this?.product?.product_properties ?? []) as Selectable[]).filter(
			(p) => !p.is_metadata,
		);
		this.productVariables = [];
		for (const selectable of selectables) {
			const isQuantity = ['quantity', 'order_quantity'].includes(selectable.key);
			const optionType = this.getOptionType(selectable.type);
			const unit = isQuantity ? this.i18n.getText(this.product?.metadata?.unit) : undefined;
			switch (optionType) {
				case 'select': {
					this.handleSelectVariable(selectable, unit);
					break;
				}
				case 'text': {
					this.handleTextVariable(selectable, unit);
					break;
				}
			}
		}
		// Hide other variables than quantity and material when in validation step
		if (this.isValidationStep) {
			for (const variable of this.productVariables) {
				if (!VISIBLE_IN_VALIDATION_STEP.has(variable.key)) variable.hidden = true;
			}
		}
		this.sortProductVariables();
	}

	/**
	 * Handles the creation of a select variable.
	 * @param selectable The selectable object to handle.
	 * @param unit The unit of the selectable.
	 */
	private handleSelectVariable(selectable: Selectable, unit?: string) {
		const variable = this.product.properties!.selectableOptions[selectable.key];
		const selectableValues = !!variable && variable.selectableValues;
		// Skip selectable variable when there are no options and fallback metadata exists
		if (this.shouldUseFallbackMetadata(selectableValues, selectable.key)) return;
		// Only items with at least 2 selectable options can become visible elements
		if (this.hasMoreThanOneSelectableValue(selectableValues)) {
			this.createSelectVariable(
				selectable.key,
				this.getInitialValue(selectable.key, selectableValues[0]),
				this.getLabelText(selectable),
				this.getPlaceholderText('select', variable, selectable),
				unit,
				this.getKeyValuePairs(selectableValues, selectable.properties?.optionValues),
			);
			return;
		}
		if (!this.hasOneSelectableValue(selectableValues)) {
			this.newRelic.noticeError(new Error('Invalid product option'), { key: selectable.key });
			return;
		}
		this.createHiddenNumberVariable(selectable.key, selectableValues[0]);
	}

	/**
	 * Determines whether to use fallback metadata for a given variable key.
	 * Fallback metadata is used when there are no variables available.
	 * @param variables - The list of variables.
	 * @param variableKey - The key of the variable to check in the product metadata.
	 * @returns A boolean indicating whether to use fallback metadata.
	 */
	private shouldUseFallbackMetadata(variables: unknown[] | undefined, variableKey: string) {
		if ((variables?.length ?? 0) > 0) return false;
		const fallbackMetadata: unknown =
			this?.product?.metadata?.[variableKey as keyof api.ProductMetadataDto];
		if (typeof fallbackMetadata !== 'string' && !Number.isFinite(fallbackMetadata)) return false;
		return true;
	}

	/** Checks if the given array of selectable values has more than one value. */
	private hasMoreThanOneSelectableValue(selectableValues?: unknown[]): boolean {
		return Array.isArray(selectableValues) && selectableValues.length > 1;
	}

	/** Checks if the given array of selectable values has only one value. */
	private hasOneSelectableValue(selectableValues?: unknown[]): boolean {
		return Array.isArray(selectableValues) && selectableValues.length === 1;
	}

	// TODO: Inline.
	private createSelectVariable(
		key: string,
		value: unknown,
		label: string,
		placeholder = '',
		unit?: string,
		keyValuePairs?: { key: string; value: unknown }[],
	) {
		this.productVariables.push({ key, value, label, type: 'select', placeholder, unit, keyValuePairs });
	}

	// TODO: Inline.
	private createHiddenNumberVariable(key: string, value: unknown) {
		this.createNumberVariable(key, value, true, '');
	}

	// TODO: Inline.
	private createNumberVariable(
		key: string,
		value: unknown,
		hidden: boolean,
		label = '',
		placeholder = '',
		unit = '',
		minValue?: number,
		maxValue?: number,
	) {
		this.productVariables.push({
			key,
			value,
			label,
			type: 'number',
			placeholder,
			unit,
			hidden,
			minValue,
			maxValue,
		});
	}

	private handleTextVariable(selectable: Selectable, unit?: string) {
		const props = this.product.properties!;
		// CSV text fields in Emmi are mapped to selectableOptions (order_quantity),
		// where as textFields contain fields that should be shown either as a number or a text input (quantity)
		// which can contain limiters like minValue & maxValue.
		const variable: Variable =
			props.textFields[selectable.key] || props.selectableOptions[selectable.key];
		const selectableValues = variable?.selectableValues;
		const placeholder = this.getPlaceholderText('text', variable, selectable);
		const label = this.getLabelText(selectable);
		// Skip text field variable when there are no options and fallback metadata exists
		if (this.shouldUseFallbackMetadata(selectableValues, selectable.key)) return;
		// "order_quantity" should always be considered a visible select element,
		// even when there's only one option
		const isVisibleSelect =
			this.hasMoreThanOneSelectableValue(selectableValues) || selectable.key === 'order_quantity';
		if (isVisibleSelect) {
			this.createSelectVariable(
				selectable.key,
				this.getInitialValue(selectable.key, selectableValues?.[0]),
				label,
				placeholder,
				unit,
				(props.selectableOptions[selectable.key].selectableValues as string[]).map((value) => ({
					key: value,
					value,
				})),
			);
			return;
		}
		if (this.hasOneSelectableValue(selectableValues)) {
			this.createHiddenNumberVariable(selectable.key, selectableValues?.[0]);
			return;
		}
		if (!this.isTextSelectable(variable)) return;
		if (this.hasVariableWithMinOrMaxValue(variable)) {
			this.createNumberVariable(
				selectable.key,
				this.getInitialValue(selectable.key, variable.minValue),
				false,
				label,
				placeholder,
				unit,
				Number.isFinite(variable.minValue) ? variable.minValue : undefined,
				Number.isFinite(variable.maxValue) ? variable.maxValue : undefined,
			);
			return;
		}
		this.createTextVariable(
			selectable.key,
			this.getInitialValue(selectable.key) as string,
			label,
			placeholder,
			unit,
		);
	}

	/** Checks if the given variable is selectable. */
	private isTextSelectable(variable?: Variable): boolean {
		return !!variable?.show;
	}

	/** Checks if the given variable has a min or max value. */
	private hasVariableWithMinOrMaxValue(variable?: Variable): boolean {
		return !!variable?.minValue || !!variable?.maxValue;
	}

	// TODO: Inline.
	private createTextVariable(key: string, value: string, label = '', placeholder = '', unit?: string) {
		this.productVariables.push({ key, value, label, type: 'text', placeholder, unit });
	}

	/** Get initial value for a given key. If the key is not present in initialOptions, the default value is returned. */
	private getInitialValue(key: string, defaultValue: unknown = undefined): unknown {
		return this.initialOptions?.[key as keyof SelectedOptions] || defaultValue;
	}

	/**
	 * Creates key/value pairs useable in <select> element. Only picks options whose key is present in selectableOptions.
	 * @param selectableOptions Array of keys representing the selectable options for this product
	 * @param optionValues Object describing all possible options ("optionValues" from API)
	 * @returns Array of key/value pairs
	 */
	private getKeyValuePairs(selectableOptions: string[], optionValues?: OptionValues) {
		return selectableOptions.map((key) => ({
			key,
			value: this.i18n.getText(optionValues?.[key]?.locale as api.LocaleStringDto),
		}));
	}

	/**
	 * Converts PIM data field type into component type shown in product editor
	 * @param fieldType Field type as returned by PIM API
	 * @returns Component type shown in product editor
	 */
	private getOptionType(fieldType: string) {
		switch (fieldType) {
			case 'MultiSelectList':
			case 'DropDown':
				return 'select';
			// Boolean is handled as a text field.
			default:
				return 'text';
		}
	}

	/**
	 * Get placeholder text used in an empty field. Uses property name by default,
	 * but most text fields will use description instead.
	 * @param type Option type returned by this.getOptionType
	 * @param variable Product specific options (selectableOptions/textFields)
	 * @param selectable Selectable option from product properties
	 * @returns Placeholder text
	 */
	private getPlaceholderText(type: string, variable: Variable, selectable: Selectable): string {
		if (type !== 'text') return this.i18n.getText(selectable.locale?.name);
		const isCustomInput = variable?.minValue || variable?.maxValue || selectable.type === 'Boolean';
		if (isCustomInput) return this.i18n.getText(selectable.locale?.description);
		return this.i18n.getText(selectable.locale?.name);
	}

	/**
	 * Get label text for empty field and as a label. Uses property name if it exists,
	 * but defaults to description texts.
	 * @param selectable Selectable option from product properties
	 * @returns Placeholder text
	 */
	private getLabelText(selectable: Selectable): string {
		return (
			this.i18n.getText(selectable.locale?.name) || this.i18n.getText(selectable.locale?.description)
		);
	}

	/**
	 * Sort option selector elements so that quantity selectors are always last and other
	 * selectors are in alphabetical order
	 */
	private sortProductVariables() {
		if (!Array.isArray(this.productVariables) || this.productVariables.length === 0) return;
		this.productVariables.sort((a, b) => {
			// If key is in "setToBottom", variable is always considered "lower priority"
			if (this.setToBottom.includes(a.key) && !this.setToBottom.includes(b.key)) return 1;
			if (!this.setToBottom.includes(a.key) && this.setToBottom.includes(b.key)) return -1;
			// Variables without labels are ignored since they aren't visible
			const labelA = a.key.startsWith('additional') ? a.label : a.placeholder;
			const labelB = b.key.startsWith('additional') ? b.label : b.placeholder;
			if (labelA && !labelB) return -1;
			if (!labelA && labelB) return 1;
			if (!labelA && !labelB) return 0;
			// Everything else is sorted alphabetically based on labels
			return labelA?.localeCompare(labelB ?? '') ?? 0;
		});
	}

	/**
	 * Construct angular reactive form and validation
	 */
	private buildForm() {
		this.requiredFields = new Set<string>();
		const formGroup: Record<string, UntypedFormControl> = {};
		// Generate form fields and validations
		for (const variable of this.productVariables) {
			const validators = [Validators.required];
			switch (variable.type) {
				case 'number':
					if (variable.minValue) validators.push(Validators.min(variable.minValue));
					if (variable.maxValue) validators.push(Validators.max(variable.maxValue));
					if (this.hasVariableWithMinOrMaxValue(variable)) {
						this.validationMessages[variable.key] = {
							required: this.customValidationMessages.number.required as string,
							min: this.customValidationMessages.number.outOfRange(variable.minValue, variable.maxValue),
							max: this.customValidationMessages.number.outOfRange(variable.minValue, variable.maxValue),
						};
						break;
					}
					this.validationMessages[variable.key] = {
						required: this.customValidationMessages.number.required as string,
					};
					break;
				case 'text':
					this.validationMessages[variable.key] = {
						required: this.customValidationMessages.number.required as string,
					};
					// Make sure "quantity" variable contains a number
					if (variable.key !== 'quantity') break;
					validators.push(Validators.pattern(/^\d+$/));
					this.validationMessages[variable.key] = {
						required: this.customValidationMessages.number.required as string,
						number: this.customValidationMessages.number.invalid as string,
					};
					break;
			}
			// Quantity must be an integer
			if (['quantity', 'order_quantity'].includes(variable.key)) {
				validators.push(Validators.pattern(/^\d+$/));
				this.validationMessages[variable.key] = {
					...this.validationMessages[variable.key],
					required: this.customValidationMessages.number.required as string,
					pattern: this.customValidationMessages.number.integerOnly as string,
				};
			}
			// All items should have the "required" validation message for [gFormAsterisk]
			const requiredMessage = this.validationMessages?.[variable.key]?.required;
			if (!requiredMessage) {
				this.validationMessages[variable.key] = {
					...this.validationMessages[variable.key],
					required: this.customValidationMessages.number.required as string,
				};
			}
			this.requiredFields.add(variable.key);
			formGroup[variable.key] = new UntypedFormControl(
				{ value: variable.value || '', disabled: this.disabled },
				validators,
			);
		}

		this.productOptionsForm = this.fb.group(formGroup);
		this.formErrors$ = formValidationErrors$(this.productOptionsForm, this.validationMessages);

		// Keep parent controller up-to-date with selected values
		this.productOptionsForm.valueChanges
			.pipe(debounceTime(500), untilDestroyed(this))
			.subscribe(() => this.emitSelectedOptionsChange());
	}

	private emitSelectedOptionsChange() {
		const errors = formValidationHelper(this.productOptionsForm, this.validationMessages);
		this.selectedOptionsChanged.emit({
			selectedOptions: this.getSelectedOptions(this.productOptionsForm.value as SelectedOptions),
			isValid: this.productOptionsForm.valid,
			errors,
		});
	}

	/** Overwrite scope if custom_scope exists. */
	private getSelectedOptions(values: SelectedOptions & { custom_scope?: string }): SelectedOptions {
		const newScope = Number.parseInt(values.custom_scope ?? '');
		return Number.isFinite(newScope) ? { ...values, scope: newScope } : values;
	}
}
